// KnowledgeBase Quasar-styled task list plugin
// Renders list items beginning with [ ] / [x] as interactive styled tasks.
// When clicked, emits a 'kb-task-toggle' CustomEvent on the document with
// the task's line number (if available) and new checked state.

import { registerMarkdownPostProcessor } from '../../services/PluginAPI.js'
import './styles/tasklists.css'

/**
 * Track task items for line number correlation
 */
let taskCounter = 0

function isEditingAllowed(context = {}) {
  if (String(context.viewMode || '').toLowerCase() === 'public') return false
  if (context.isPublicView === true) return false
  return context.canEdit !== false
}

function getTableLocation(cell, root) {
  const table = cell?.closest?.('table')
  const row = cell?.closest?.('tr')
  if (!table || !row || !root) return null

  const tableIndex = Array.from(root.querySelectorAll('table')).indexOf(table)
  const rowIndex = Array.from(table.querySelectorAll('tr')).indexOf(row)
  const columnIndex = Array.from(row.children).filter((el) => /^(TD|TH)$/.test(el.tagName)).indexOf(cell)

  if (tableIndex < 0 || rowIndex < 0 || columnIndex < 0) return null
  return { tableIndex, rowIndex, columnIndex }
}

/**
 * Process a single table cell — replaces [ ] / [x] markers with interactive checkboxes
 */
function processTableCell(cell, root, context = {}) {
  if (!cell || cell.classList.contains('kb-task-cell')) return false

  const walker = document.createTreeWalker(cell, NodeFilter.SHOW_TEXT, null)
  let textNode = null
  let m = null
  while (true) {
    const n = walker.nextNode()
    if (!n) break
    const raw = String(n.nodeValue || '')
    // Use \s* (not \s+) so a lone [ ] with nothing after still matches
    const mm = raw.match(/^\s*\[( |x|X)\]\s*/)
    if (mm) {
      textNode = n
      m = mm
      break
    }
  }
  if (!textNode || !m) return false

  const checked = m[1].toLowerCase() === 'x'
  const taskId = `kb-task-${++taskCounter}`

  // Prefer full cell text (post-marker) so cases like [x] [Link](url) work
  const fullCellText = String(cell.textContent || '')
  const taskText = fullCellText.replace(/^\s*\[( |x|X)\]\s*/, '').trim()
  // Still remove the marker from the first text node
  textNode.nodeValue = String(textNode.nodeValue || '').replace(/^\s*\[( |x|X)\]\s*/, '')

  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.checked = checked
  checkbox.className = 'kb-task-checkbox'
  checkbox.id = taskId
  checkbox.setAttribute('aria-label', 'Toggle task completion')
  checkbox.dataset.taskId = taskId
  checkbox.dataset.taskText = taskText
  checkbox.dataset.wasChecked = checked ? 'true' : 'false'
  checkbox.disabled = !isEditingAllowed(context)

  const box = document.createElement('label')
  box.className = `kb-taskbox ${checked ? 'is-checked' : 'is-unchecked'}${
    checkbox.disabled ? ' is-disabled' : ''
  }`
  box.setAttribute('for', taskId)
  if (checkbox.disabled) box.setAttribute('aria-disabled', 'true')
  box.appendChild(checkbox)

  if (!checkbox.disabled) {
    checkbox.addEventListener('change', (e) => {
      const newChecked = e.target.checked
      const wasChecked = checkbox.dataset.wasChecked === 'true'
      const tableLocation = getTableLocation(cell, root)
      box.classList.toggle('is-checked', newChecked)
      box.classList.toggle('is-unchecked', !newChecked)

      document.dispatchEvent(
        new CustomEvent('kb-task-toggle', {
          bubbles: true,
          detail: {
            taskId,
            checked: newChecked,
            wasChecked,
            taskText: checkbox.dataset.taskText,
            taskSource: 'table',
            tableIndex: tableLocation?.tableIndex ?? null,
            rowIndex: tableLocation?.rowIndex ?? null,
            columnIndex: tableLocation?.columnIndex ?? null,
            element: cell
          }
        })
      )

      checkbox.dataset.wasChecked = newChecked ? 'true' : 'false'
    })
  }

  cell.insertBefore(box, cell.firstChild)
  cell.classList.add('kb-task-cell')

  return true
}

/**
 * Process a single list item into an interactive task
 */
function processListItem(li, context = {}) {
  if (!li || li.classList.contains('kb-task-item')) return false

  // Find the first text node that starts with a task marker [ ] or [x]
  const walker = document.createTreeWalker(li, NodeFilter.SHOW_TEXT, null)
  let textNode = null
  let m = null
  while (true) {
    const n = walker.nextNode()
    if (!n) break
    const raw = String(n.nodeValue || '')
    const mm = raw.match(/^\s*\[( |x|X)\]\s+/)
    if (mm) {
      textNode = n
      m = mm
      break
    }
  }
  if (!textNode || !m) return false

  const checked = m[1].toLowerCase() === 'x'
  const taskId = `kb-task-${++taskCounter}`

  // Compute task text from the full list item text (after marker), not just the first text node,
  // so cases like [x] [Link](...) are captured.
  const fullItemText = String(li.textContent || '')
  const taskText = fullItemText.replace(/^\s*\[( |x|X)\]\s+/, '').trim()
  // Remove marker from the matched text node for visual cleanup
  textNode.nodeValue = String(textNode.nodeValue || '').replace(/^\s*\[( |x|X)\]\s+/, '')

  // Create an interactive checkbox
  const checkbox = document.createElement('input')
  checkbox.type = 'checkbox'
  checkbox.checked = checked
  checkbox.className = 'kb-task-checkbox'
  checkbox.id = taskId
  checkbox.setAttribute('aria-label', 'Toggle task completion')
  checkbox.disabled = !isEditingAllowed(context)

  // Store task text for content matching
  checkbox.dataset.taskId = taskId
  checkbox.dataset.taskText = taskText
  checkbox.dataset.wasChecked = checked ? 'true' : 'false'

  // Create the visual box that wraps the checkbox
  const box = document.createElement('label')
  box.className = `kb-taskbox ${checked ? 'is-checked' : 'is-unchecked'}${
    checkbox.disabled ? ' is-disabled' : ''
  }`
  box.setAttribute('for', taskId)
  if (checkbox.disabled) box.setAttribute('aria-disabled', 'true')
  box.appendChild(checkbox)

  // Handle toggle interaction
  if (!checkbox.disabled) {
    checkbox.addEventListener('change', (e) => {
      const newChecked = e.target.checked
      const wasChecked = checkbox.dataset.wasChecked === 'true'
      box.classList.toggle('is-checked', newChecked)
      box.classList.toggle('is-unchecked', !newChecked)
      li.classList.toggle('kb-task-checked', newChecked)

      // Emit custom event for the editor to handle content update
      const event = new CustomEvent('kb-task-toggle', {
        bubbles: true,
        detail: {
          taskId,
          checked: newChecked,
          wasChecked,
          taskText: checkbox.dataset.taskText,
          taskSource: 'list',
          element: li
        }
      })
      document.dispatchEvent(event)

      // Update dataset for next toggle
      checkbox.dataset.wasChecked = newChecked ? 'true' : 'false'
    })
  }

  li.insertBefore(box, li.firstChild)
  li.classList.add('kb-task-item')
  if (checked) li.classList.add('kb-task-checked')
  li.dataset.taskId = taskId

  return true
}

/**
 * Reset task counter (useful for consistent numbering in tests)
 */
export function resetTaskCounter() {
  taskCounter = 0
}

/**
 * Get the current task counter value
 */
export function getTaskCount() {
  return taskCounter
}

export function registerQuasarTaskListsPlugin() {
  registerMarkdownPostProcessor(
    (root, context = {}) => {
      // Reset counter for each render to get consistent task IDs within a note
      taskCounter = 0

      const items = Array.from(root.querySelectorAll('li'))
      let lineEstimate = 0

      for (const li of items) {
        try {
          // Estimate line number based on position in document
          // This is a rough heuristic - actual line correlation requires source mapping
          lineEstimate++
          processListItem(li, { ...context, lineNumber: lineEstimate })
        } catch {
          /* noop */
        }
      }

      // Also render [ ] / [x] checkboxes inside table cells (td and th)
      const cells = Array.from(root.querySelectorAll('td, th'))
      for (const cell of cells) {
        try {
          processTableCell(cell, root, context)
        } catch {
          /* noop */
        }
      }
    },
    { sortOrder: 50 }
  )
}

export default { registerQuasarTaskListsPlugin, resetTaskCounter, getTaskCount }
